import '@vaadin/text-area';
// eslint-disable-next-line no-duplicate-imports
import type { TextAreaValueChangedEvent, TextArea as VaadinTextArea } from '@vaadin/text-area';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { TmValueChanged } from './forms';
import { TmInputFieldBase } from './tm-input-field.base';

export type TmTextAreaValueChanged = TmValueChanged<string>;

export const tagName = 'tm-text-area';
@customElement(tagName)
export class TmTextArea extends TmInputFieldBase<string> {
    @property({ type: String })
    public allowedCharacters?: string;

    @property({ type: String })
    public pattern?: string;

    @property({ type: Number })
    public minLength?: number;

    @property({ type: Number })
    public maxLength?: number;

    @property({ type: Boolean })
    public hasBottomBorder: boolean = true;

    private readonly textAreaRef = createRef<VaadinTextArea>();

    protected validate() {
        if (!this.textAreaRef.value) {
            return false;
        }

        return this.textAreaRef.value.checkValidity();
    }

    protected render() {
        return html`
            <vaadin-text-area
                ${ref(this.textAreaRef)}
                @value-changed=${(e: TextAreaValueChangedEvent) => {
                    this.changeValue(e.detail.value);
                }}
                class=${classMap({
                    'w-full': true,
                    'border-b border-gray-400-50': this.hasBottomBorder,
                })}
                part="component"
                label=${ifDefined(this.label)}
                value=${ifDefined(this.initial)}
                placeholder=${ifDefined(this.placeholder)}
                pattern=${ifDefined(this.pattern)}
                allowed-char-pattern=${ifDefined(this.allowedCharacters)}
                helper-text=${ifDefined(this.helperText)}
                .minlength=${this.minLength}
                .maxlength=${this.maxLength}
                ?required=${this.required}
                ?readonly=${this.readonly}
                ?disabled=${this.disabled}
                has-placeholder
            >
            </vaadin-text-area>
        `;
    }

    public static get styles() {
        return [
            ...super.styles,
            css`
                :host {
                    display: flex;
                }

                :host(.form-row-slotted) {
                    margin: 0;
                }

                vaadin-text-area {
                    textarea {
                        padding: 0.5rem;
                        border-radius: 10px 10px 0 0;
                        resize: none;
                    }
                }

                textarea::placeholder {
                    opacity: 0.5;
                }

                vaadin-text-area::part(helper-text) {
                    color: var(--secondary-text-color);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmTextArea;
    }
}
